/**
 * Compliance Center — Regulatory Applicability (spine view).
 *
 * Phase 3 rebuild (memory/REBUILD_PLAN_SPINE_VIEWS.md): replaces the 704-line
 * mock "Regulatory Exposure Control Tower" with a thin view over the REAL
 * FrameworkApplicability spine table. Marcus's question — "which regulations
 * apply to us, why, and by when" — answered from backend verdicts, not
 * hardcoded state. Verdicts recompute from the entity profile; this page only
 * reads.
 *
 * Data: GET /v1/entity/profile · /v1/entity/frameworks ·
 *       /v1/entity/applicability-rules · /v1/entity/scope-summary
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  C, api, Badge, Btn, Card, DetailPanel, DrillRow, EmptyState, Err, KV,
  KPICard, Label, Loader, Breadcrumb,
} from './journeys/shared';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const VERDICT = {
  mandatory: { color: C.red, label: 'MANDATORY' },
  conditional: { color: C.amber, label: 'CONDITIONAL' },
  voluntary: { color: C.blue, label: 'VOLUNTARY' },
  not_applicable: { color: C.dim, label: 'NOT APPLICABLE' },
};

const verdictOrder = { mandatory: 0, conditional: 1, voluntary: 2, not_applicable: 3 };

function nextDeadline(frameworks) {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = frameworks
    .filter((f) => f.filing_deadline && f.filing_deadline >= today
      && f.applicability !== 'not_applicable')
    .sort((a, b) => a.filing_deadline.localeCompare(b.filing_deadline));
  return upcoming[0] || null;
}

export default function ComplianceCenter() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [frameworks, setFrameworks] = useState(null);
  const [rules, setRules] = useState([]);
  const [scope, setScope] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noProfile, setNoProfile] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, f, r, s] = await Promise.all([
        api('/v1/entity/profile'),
        api('/v1/entity/frameworks'),
        api('/v1/entity/applicability-rules'),
        api('/v1/entity/scope-summary'),
      ]);
      if (p?._error && p.status === 404) {
        setNoProfile(true);
      } else if (p?._error || f?._error) {
        setError((p?._error ? p.message : f.message) || 'Failed to load applicability data');
      } else {
        setProfile(p);
        setFrameworks((f.frameworks || []).sort(
          (a, b) => (verdictOrder[a.applicability] ?? 9) - (verdictOrder[b.applicability] ?? 9)
            || a.framework_id.localeCompare(b.framework_id)));
        if (!r?._error) setRules(r.rules || []);
        if (!s?._error) setScope(s);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 24, background: C.bg, minHeight: '100vh' }}>
        <Loader text="Loading regulatory applicability..." />
      </div>
    );
  }

  if (noProfile) {
    return (
      <div style={{ padding: 24, background: C.bg, minHeight: '100vh' }}>
        <Breadcrumb path={[{ label: 'Compliance' }, { label: 'Regulatory Applicability' }]} />
        <EmptyState
          icon="🏛️"
          title="No entity profile yet"
          message="Applicability verdicts are computed from your entity profile (jurisdiction, headcount, revenue, listing status, sector). Create the profile to run the determination."
          action="Set up entity profile"
          onAction={() => navigate('/system/organization')}
        />
      </div>
    );
  }

  const mandatory = frameworks?.filter((f) => f.applicability === 'mandatory') || [];
  const conditional = frameworks?.filter((f) => f.applicability === 'conditional') || [];
  const next = frameworks ? nextDeadline(frameworks) : null;
  const ruleFor = (fid) => rules.find(
    (r) => r.framework === fid || r.framework.startsWith(fid) || fid.startsWith(r.framework.split('/')[0]));

  return (
    <div style={{ padding: 24, background: C.bg, minHeight: '100vh', color: C.text }}>
      <Breadcrumb path={[{ label: 'Compliance' }, { label: 'Regulatory Applicability' }]} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Regulatory Applicability</div>
          <div style={{ fontSize: 10, color: C.muted }}>
            Verdicts derived from the entity profile — every row traces to a determination rule
          </div>
        </div>
        <Btn onClick={() => navigate('/obligations/register')} color={C.blue}>
          Open obligation register →
        </Btn>
      </div>

      <Err error={error} />

      {/* Exposure summary */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <KPICard label="Applicable frameworks" color={C.blue}
          value={scope?.framework_count ?? (mandatory.length + conditional.length)}
          sub={scope?.entity ? `entity: ${scope.entity}` : undefined} />
        <KPICard label="Mandatory" value={mandatory.length} color={C.red}
          sub={mandatory.map((f) => f.framework_id).join(' · ') || 'none'} />
        <KPICard label="Conditional" value={conditional.length} color={C.amber} />
        <KPICard label="Next filing deadline" color={next ? C.amber : C.emerald}
          value={<span style={MONO}>{next ? next.filing_deadline : '—'}</span>}
          sub={next ? next.framework_id : 'no upcoming deadlines'} />
        <KPICard label="Disclosure requirements" value={scope?.requirement_count ?? '—'}
          sub={scope ? `${scope.topic_count} topics` : undefined}
          onClick={() => navigate('/obligations/register')} />
      </div>

      {/* Applicability matrix */}
      <Card>
        <Label>Applicability matrix</Label>
        <div style={{ display: 'flex', padding: '4px 10px', fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <span style={{ flex: 2 }}>Framework</span>
          <span style={{ flex: 1.2 }}>Verdict</span>
          <span style={{ flex: 1 }}>Phase</span>
          <span style={{ flex: 1 }}>First reporting</span>
          <span style={{ flex: 1.2 }}>Filing deadline</span>
        </div>
        {(frameworks || []).map((f) => {
          const v = VERDICT[f.applicability] || VERDICT.not_applicable;
          return (
            <DrillRow key={f.framework_id} active={selected?.framework_id === f.framework_id}
              onClick={() => setSelected(selected?.framework_id === f.framework_id ? null : f)}>
              <span style={{ flex: 2, fontWeight: 600, fontSize: 12 }}>
                {f.framework_id}
                {f.user_override && (
                  <span style={{ marginLeft: 6 }}><Badge text="OVERRIDE" color={C.purple} /></span>
                )}
              </span>
              <span style={{ flex: 1.2 }}><Badge text={v.label} color={v.color} /></span>
              <span style={{ flex: 1, fontSize: 10, color: C.muted }}>{f.phase || '—'}</span>
              <span style={{ flex: 1, fontSize: 10, ...MONO }}>{f.first_reporting_year || '—'}</span>
              <span style={{ flex: 1.2, fontSize: 10, ...MONO }}>{f.filing_deadline || '—'}</span>
            </DrillRow>
          );
        })}
        {frameworks && !frameworks.length && (
          <EmptyState title="No applicability verdicts yet"
            message="Save the entity profile to trigger the determination engine." />
        )}
      </Card>

      {/* Drill-down: verdict provenance */}
      {selected && (
        <DetailPanel
          title={selected.framework_id}
          subtitle="Verdict provenance — why this framework applies"
          color={(VERDICT[selected.applicability] || VERDICT.not_applicable).color}
          onClose={() => setSelected(null)}
          actions={<Btn small color={C.blue} onClick={() => navigate('/obligations/register')}>Obligations →</Btn>}
        >
          <KV label="Verdict" value={(VERDICT[selected.applicability] || VERDICT.not_applicable).label}
            color={(VERDICT[selected.applicability] || VERDICT.not_applicable).color} />
          <KV label="Determination reason" value={selected.reason || '—'} />
          <KV label="Trigger rule" value={ruleFor(selected.framework_id)?.trigger || '—'} />
          <KV label="Phase" value={selected.phase || '—'} />
          <KV label="Effective from" value={<span style={MONO}>{selected.effective_from || '—'}</span>} />
          <KV label="First reporting year" value={<span style={MONO}>{selected.first_reporting_year || '—'}</span>} />
          <KV label="Filing deadline" value={<span style={MONO}>{selected.filing_deadline || '—'}</span>} />
          <KV label="User override" value={selected.user_override ? 'yes' : 'no'}
            color={selected.user_override ? C.purple : undefined} />
        </DetailPanel>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Determination inputs (the profile that drives verdicts) */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <Label>Determination inputs</Label>
            <Btn small color={C.blue} onClick={() => navigate('/system/organization')}>Edit profile</Btn>
          </div>
          <KV label="Entity" value={profile?.name || '—'} />
          <KV label="Jurisdiction" value={profile?.jurisdiction || '—'} />
          <KV label="Sector" value={profile?.sector || '—'} />
          <KV label="Employees" value={<span style={MONO}>{profile?.employee_count ?? '—'}</span>} />
          <KV label="Revenue (EUR)" value={<span style={MONO}>{profile?.revenue_eur != null ? Number(profile.revenue_eur).toLocaleString() : '—'}</span>} />
          <KV label="Listed" value={profile?.listed ? 'yes' : 'no'} />
          <div style={{ fontSize: 9, color: C.dim, marginTop: 8 }}>
            Changing the profile re-runs the determination engine; verdicts above update on save.
          </div>
        </Card>

        {/* Rulebook */}
        <Card>
          <Label>Determination rulebook</Label>
          {rules.map((r) => (
            <KV key={r.framework} label={r.framework} value={r.trigger} />
          ))}
          {!rules.length && <div style={{ fontSize: 10, color: C.dim }}>Rulebook unavailable</div>}
        </Card>
      </div>
    </div>
  );
}
